import { DomainNameValidationResult, StandardRecordMetadataKey, normalizeDomainName } from '@tezos-domains/core';
import NodeCache from 'node-cache';

class NullNameResolver {
    resolveDomainRecord() {
        return Promise.resolve(null);
    }
    resolveNameToAddress() {
        return Promise.resolve(null);
    }
    resolveReverseRecord() {
        return Promise.resolve(null);
    }
    resolveAddressToName() {
        return Promise.resolve(null);
    }
    clearCache() {
        return void 0;
    }
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

class BlockchainNameResolver {
    constructor(tezosDomainsDataProvider, tracer, validator) {
        this.tezosDomainsDataProvider = tezosDomainsDataProvider;
        this.tracer = tracer;
        this.validator = validator;
    }
    resolveDomainRecord(name) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Resolving record '${name}'`);
            if (!name) {
                throw new Error(`Argument 'name' was not specified.`);
            }
            if (this.validator.validateDomainName(name) !== DomainNameValidationResult.VALID) {
                throw new Error(`'${name}' is not a valid domain name.`);
            }
            const record = yield this.tezosDomainsDataProvider.resolveDomainInfo(name);
            this.tracer.trace(`<= Resolved record.`, record);
            return record;
        });
    }
    resolveNameToAddress(name) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Resolving address for '${name}'`);
            const record = yield this.resolveDomainRecord(name);
            const address = (record === null || record === void 0 ? void 0 : record.address) || null;
            this.tracer.trace(`<= Resolved address.`, address);
            return address;
        });
    }
    resolveReverseRecord(address) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Resolving reverse record for '${address}'`);
            if (!address) {
                throw new Error(`Argument 'address' was not specified.`);
            }
            const reverseRecordDomain = yield this.tezosDomainsDataProvider.resolveReverseRecordDomainInfo(address);
            this.tracer.trace(`<= Resolved reverse record.`, reverseRecordDomain);
            return reverseRecordDomain;
        });
    }
    resolveAddressToName(address) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Resolving name for '${address}'`);
            const reverseRecordDomain = yield this.resolveReverseRecord(address);
            if (!reverseRecordDomain) {
                return null;
            }
            this.tracer.trace(`<= Resolved name.`, reverseRecordDomain.name);
            return reverseRecordDomain.name;
        });
    }
    clearCache() {
        return void 0;
    }
}

class CachedNameResolver {
    constructor(inner, tracer, config) {
        this.inner = inner;
        this.tracer = tracer;
        this.config = config;
        this.cache = new NodeCache({ useClones: false });
    }
    resolveDomainRecord(name) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Cache decorator: Resolving record '${name}'.`);
            if (!this.cache.has(name)) {
                this.tracer.trace('!! Cache miss.');
                const promise = this.inner.resolveDomainRecord(name);
                promise
                    .then(r => {
                    if (!r) {
                        return null;
                    }
                    const ttl = r.data.getJson(StandardRecordMetadataKey.TTL);
                    if (ttl) {
                        this.cache.ttl(name, ttl);
                    }
                })
                    .catch((err) => {
                    this.tracer.trace(`!! Removing ${name} from cache because resolving failed.`, err);
                    this.cache.del(name);
                });
                this.cache.set(name, promise, this.config.defaultRecordTtl);
            }
            else {
                this.tracer.trace('!! Cache hit.');
            }
            const promise = this.cache.get(name);
            const record = yield promise;
            this.tracer.trace(`<= Cache decorator: Resolved address.`, record);
            return record;
        });
    }
    resolveNameToAddress(name) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Cache decorator: Resolving address for '${name}'`);
            const record = yield this.resolveDomainRecord(name);
            const address = (record === null || record === void 0 ? void 0 : record.address) || null;
            this.tracer.trace(`<= Cache decorator: Resolved address.`, address);
            return address;
        });
    }
    resolveReverseRecord(address) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Cache decorator Resolving reverse record '${address}'`);
            if (!this.cache.has(address)) {
                this.tracer.trace('!! Cache miss.');
                const promise = this.inner.resolveReverseRecord(address);
                promise
                    .then(r => {
                    if (!r) {
                        return null;
                    }
                    const ttl = r.data.getJson(StandardRecordMetadataKey.TTL);
                    if (ttl) {
                        this.cache.ttl(address, ttl);
                    }
                })
                    .catch((err) => {
                    this.tracer.trace(`!! Removing ${address} from cache because resolving failed.`, err);
                    this.cache.del(address);
                });
                this.cache.set(address, promise, this.config.defaultReverseRecordTtl);
            }
            else {
                this.tracer.trace('!! Cache hit.');
            }
            const promise = this.cache.get(address);
            const reverseRecord = yield promise;
            this.tracer.trace(`<= Cache decorator: Resolved reverse record.`, reverseRecord);
            return reverseRecord;
        });
    }
    resolveAddressToName(address) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Cache decorator: Resolving name for '${address}'`);
            const reverseRecordDomain = yield this.resolveReverseRecord(address);
            const name = (reverseRecordDomain === null || reverseRecordDomain === void 0 ? void 0 : reverseRecordDomain.name) || null;
            this.tracer.trace(`<= Cache decorator: Resolved name.`, name);
            return name;
        });
    }
    clearCache() {
        this.cache.flushAll();
    }
}

class NameNormalizingNameResolver {
    constructor(inner, tracer) {
        this.inner = inner;
        this.tracer = tracer;
    }
    resolveDomainRecord(name) {
        return this.inner.resolveDomainRecord(this.normalizeName(name));
    }
    resolveNameToAddress(name) {
        return this.inner.resolveNameToAddress(this.normalizeName(name));
    }
    resolveReverseRecord(address) {
        return this.inner.resolveReverseRecord(address);
    }
    resolveAddressToName(address) {
        return this.inner.resolveAddressToName(address);
    }
    clearCache() {
        this.inner.clearCache();
    }
    normalizeName(name) {
        const normalized = normalizeDomainName(name);
        if (name !== normalized) {
            this.tracer.trace(`!! Normalized name '${name}' to '${normalized}'.`);
        }
        return normalized;
    }
}

function createResolver(config, dataProvider, tracer, validator) {
    let resolver = new BlockchainNameResolver(dataProvider, tracer, validator);
    if (config.caching) {
        resolver = new CachedNameResolver(resolver, tracer, {
            defaultRecordTtl: config.caching.defaultRecordTtl || 600,
            defaultReverseRecordTtl: config.caching.defaultReverseRecordTtl || 600,
        });
    }
    resolver = new NameNormalizingNameResolver(resolver, tracer);
    return resolver;
}

export { NullNameResolver, createResolver };
//# sourceMappingURL=resolver.es2015.js.map
